import { TimelinesGridPrecisionUnit, UnitOfTime } from '@ardoq/api-types';
import {
  minGridLineDistances,
  POPOVER_ELEMENT_TYPE,
  unitQuantities,
} from './consts';
import { TEXT_2_BOLD_REACT_STYLE } from './items/consts';
import { Locale } from '@ardoq/locale';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';

const { fontSize, lineHeight, fontFamily } = TEXT_2_BOLD_REACT_STYLE;

export const measureGridLabelTextWidth = canvasLabelMeasurer(
  `400 ${fontSize}/${lineHeight} ${fontFamily}`
);

export const measureGridLabelBoldTextWidth = canvasLabelMeasurer(
  `600 ${fontSize}/${lineHeight} ${fontFamily}`
);

// Item labels (titles, captions) produce more unique measured strings than grid labels
// (which are mostly formatted dates), so use a larger cache.
export const measureOtherLabelWidth = canvasLabelMeasurer(
  `400 ${fontSize}/${lineHeight} ${fontFamily}`,
  1000
);

export const getSmallestTimelineGrid = () =>
  (Object.keys(minGridLineDistances) as TimelinesGridPrecisionUnit[]).reduce(
    (acc, key) => (unitQuantities[acc] < unitQuantities[key] ? acc : key),
    UnitOfTime.DAY
  );

export const getBiggestTimelineGrid = () =>
  (Object.keys(minGridLineDistances) as TimelinesGridPrecisionUnit[]).reduce(
    (acc, key) => (unitQuantities[acc] > unitQuantities[key] ? acc : key),
    UnitOfTime.DAY
  );

export const getLabelLinesCount = (
  text: string,
  width: number,
  limit: number
) => {
  const spaceWidth = measureOtherLabelWidth(' ');

  const words = text.split(' ');

  let lines = 1;
  let currentLineWidth = 0;

  for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
    const word = words[wordIndex];
    const wordWidth = measureOtherLabelWidth(word);

    if (currentLineWidth + wordWidth > width) {
      lines++;
      currentLineWidth = 0;
    }
    if (lines === limit) {
      break;
    }

    currentLineWidth += wordWidth;

    if (wordIndex < words.length - 1) {
      currentLineWidth += spaceWidth;
    }
  }

  return lines;
};

type FormatDateBasedOnPrecisionArgs = {
  date: Date;
  precision: UnitOfTime;
  isLocaleTimeFormat12Hour: boolean;
  currentLocale: Locale;
  excludeTime?: boolean;
};

/**
 * Format a date based on precision specified in args
 */
export const formatDateBasedOnPrecision = ({
  date,
  precision,
  currentLocale,
  isLocaleTimeFormat12Hour,
  excludeTime = false,
}: FormatDateBasedOnPrecisionArgs) => {
  const precisionValue = unitQuantities[precision];

  const formatOptions: Intl.DateTimeFormatOptions = {};

  const showHours =
    !excludeTime && precisionValue <= unitQuantities[UnitOfTime.DAY];

  // always show minutes with hours when the time format is 24h. otherwise it just looks like "23"
  // which is confusing. 12-hour formats should have the AM/PM suffix.
  const showMinutes = showHours && !isLocaleTimeFormat12Hour;

  if (showMinutes) {
    formatOptions.minute = 'numeric';
  }
  if (showHours) {
    formatOptions.hour = 'numeric';
  }

  formatOptions.day = 'numeric';
  formatOptions.month = 'numeric';
  formatOptions.year = 'numeric';

  return new Intl.DateTimeFormat(currentLocale.code, formatOptions).format(
    date
  );
};

export const isPopoverElementType = (
  type: string | null
): type is POPOVER_ELEMENT_TYPE => {
  return (
    type !== null &&
    Object.values(POPOVER_ELEMENT_TYPE).includes(type as POPOVER_ELEMENT_TYPE)
  );
};
